/**
 * Multi-layer cipher encoder/decoder.
 *
 * Supports:
 * - Phrase substitutions (longest match first)
 * - Character cipher (vowel rotation, consonant shift)
 * - Plural handling (s, es, ies)
 * - Case preservation
 * - Bidirectional encode/decode
 * - Canonical forms for ambiguous decode mappings
 */

/**
 * Build encoding and decoding maps from substitutions.
 *
 * Substitution values can be:
 * - Simple string: "Kill": "Close Account"
 * - Object with canonical flag: "Kill": { cipher: "Close Account", canonical: true }
 *
 * @param {Object} substitutions - The substitutions object from cipher config
 * @returns {{ encodeMap: Object, decodeMap: Object }}
 */
export function buildMaps(substitutions = {}) {
  const encodeMap = {}
  const decodeMap = {}

  for (const [plain, cipherVal] of Object.entries(substitutions)) {
    let cipher, isCanonical

    if (typeof cipherVal === 'object' && cipherVal !== null) {
      cipher = cipherVal.cipher
      isCanonical = cipherVal.canonical || false
    } else {
      cipher = cipherVal
      isCanonical = false
    }

    encodeMap[plain.toLowerCase()] = cipher

    const cipherLower = cipher.toLowerCase()
    if (!decodeMap[cipherLower]) {
      decodeMap[cipherLower] = { canonical: null, options: [] }
    }

    decodeMap[cipherLower].options.push(plain)
    if (isCanonical) {
      decodeMap[cipherLower].canonical = plain
    }
  }

  return { encodeMap, decodeMap }
}

/**
 * Apply case pattern from original to replacement.
 * @param {string} original - Original text to get case pattern from
 * @param {string} replacement - Text to apply case pattern to
 * @param {boolean} preserve - Whether to preserve case
 * @returns {string}
 */
export function applyCase(original, replacement, preserve = true) {
  if (!preserve || !replacement || !original) {
    return replacement
  }

  if (original === original.toUpperCase() && original !== original.toLowerCase()) {
    return replacement.toUpperCase()
  }
  if (original === original.toLowerCase()) {
    return replacement.toLowerCase()
  }
  if (original[0] === original[0].toUpperCase()) {
    return replacement[0].toUpperCase() + replacement.slice(1)
  }
  return replacement
}

/**
 * Build consonant alphabet by excluding vowels.
 * @param {string[]} vowels - Array of uppercase vowel characters
 * @returns {string[]}
 */
function buildConsonants(vowels) {
  const vowelSet = new Set(vowels)
  const consonants = []
  for (let i = 0; i < 26; i++) {
    const char = String.fromCharCode(65 + i)
    if (!vowelSet.has(char)) {
      consonants.push(char)
    }
  }
  return consonants
}

/**
 * Encode a single character using character cipher config.
 * @param {string} c - Single character
 * @param {Object} cfg - Character cipher config { vowelRotation, consonantShift }
 * @returns {string}
 */
export function encodeChar(c, cfg) {
  if (!c || !/[A-Za-z]/.test(c)) {
    return c
  }

  const vowels = cfg.vowelRotation || ['A', 'E', 'I', 'O', 'U', 'Y']
  const shift = cfg.consonantShift || 2

  const upper = c.toUpperCase()
  const isLower = c === c.toLowerCase()

  let result
  if (vowels.includes(upper)) {
    const idx = vowels.indexOf(upper)
    result = vowels[(idx + 1) % vowels.length]
  } else {
    // Shift within consonants only to avoid hitting vowels
    const consonants = buildConsonants(vowels)
    const idx = consonants.indexOf(upper)
    if (idx === -1) {
      // Shouldn't happen, but fallback
      result = upper
    } else {
      result = consonants[(idx + shift) % consonants.length]
    }
  }

  return isLower ? result.toLowerCase() : result
}

/**
 * Decode a single character using character cipher config.
 * @param {string} c - Single character
 * @param {Object} cfg - Character cipher config { vowelRotation, consonantShift }
 * @returns {string}
 */
export function decodeChar(c, cfg) {
  if (!c || !/[A-Za-z]/.test(c)) {
    return c
  }

  const vowels = cfg.vowelRotation || ['A', 'E', 'I', 'O', 'U', 'Y']
  const shift = cfg.consonantShift || 2

  const upper = c.toUpperCase()
  const isLower = c === c.toLowerCase()

  let result
  if (vowels.includes(upper)) {
    const idx = vowels.indexOf(upper)
    result = vowels[(idx - 1 + vowels.length) % vowels.length]
  } else {
    // Shift within consonants only (reverse of encode)
    const consonants = buildConsonants(vowels)
    const idx = consonants.indexOf(upper)
    if (idx === -1) {
      // Shouldn't happen, but fallback
      result = upper
    } else {
      result = consonants[(idx - shift + consonants.length) % consonants.length]
    }
  }

  return isLower ? result.toLowerCase() : result
}

/**
 * Apply character cipher to an entire word.
 * @param {string} word - Word to process
 * @param {Object} cfg - Character cipher config
 * @param {boolean} decode - Whether to decode (false = encode)
 * @returns {string}
 */
export function charCipherWord(word, cfg, decode = false) {
  const fn = decode ? decodeChar : encodeChar
  return word.split('').map(c => fn(c, cfg)).join('')
}

/**
 * Escape special regex characters in a string.
 * @param {string} str
 * @returns {string}
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Apply character cipher to unmatched text (words that didn't match substitutions).
 * @param {string} text
 * @param {Object|null} charCfg - Character cipher config or null
 * @param {string} mode - 'encode' or 'decode'
 * @returns {string}
 */
function processUnmatched(text, charCfg, mode) {
  if (!charCfg || !text) {
    return text
  }

  const decode = mode === 'decode'
  return text.replace(/[A-Za-z]+/g, match => charCipherWord(match, charCfg, decode))
}

/**
 * Process text through the cipher.
 *
 * @param {string} text - Input text
 * @param {Object} cipher - Cipher definition object
 * @param {string} mode - 'encode' or 'decode'
 * @returns {string}
 */
export function process(text, cipher, mode) {
  const preserve = cipher.options?.preserveCase !== false
  const charCfg = cipher.characterCipher || cipher.character_cipher || null
  const substitutions = cipher.substitutions || {}

  const { encodeMap, decodeMap } = buildMaps(substitutions)
  const subMap = mode === 'encode' ? encodeMap : decodeMap

  const keys = Object.keys(subMap)
  if (keys.length === 0) {
    // No substitutions, just character cipher everything
    return processUnmatched(text, charCfg, mode)
  }

  // Build regex for substitution matching (longest first, with optional plural suffix)
  const sortedKeys = [...keys].sort((a, b) => b.length - a.length)
  const pattern = new RegExp(
    '\\b(' + sortedKeys.map(escapeRegex).join('|') + ')(ies|es|s)?\\b',
    'gi'
  )

  const result = []
  let lastEnd = 0
  let match

  while ((match = pattern.exec(text)) !== null) {
    // Handle text before this match
    const before = text.slice(lastEnd, match.index)
    result.push(processUnmatched(before, charCfg, mode))

    const baseText = match[1]
    const suffix = match[2] || ''

    // Get replacement
    let repl
    if (mode === 'encode') {
      repl = subMap[baseText.toLowerCase()]
    } else {
      const entry = subMap[baseText.toLowerCase()]
      repl = entry.canonical || entry.options[0]
    }

    // Apply case preservation
    repl = applyCase(baseText, repl, preserve)
    const casedSuffix = suffix ? applyCase(baseText, suffix, preserve) : ''

    result.push(repl + casedSuffix)
    lastEnd = match.index + match[0].length
  }

  // Handle remaining text after last match
  result.push(processUnmatched(text.slice(lastEnd), charCfg, mode))

  return result.join('')
}

/**
 * Encode text using a cipher.
 * @param {string} text - Plain text
 * @param {Object} cipher - Cipher definition
 * @returns {string}
 */
export function encode(text, cipher) {
  return process(text, cipher, 'encode')
}

/**
 * Decode text using a cipher.
 * @param {string} text - Cipher text
 * @param {Object} cipher - Cipher definition
 * @returns {string}
 */
export function decode(text, cipher) {
  return process(text, cipher, 'decode')
}

/**
 * Build a codebook (substitution table) for display.
 * Returns an array of { plain, cipher, isCanonical } sorted by plain text.
 *
 * @param {Object} substitutions - The substitutions object from cipher config
 * @returns {Array<{ plain: string, cipher: string, isCanonical: boolean }>}
 */
export function buildCodebook(substitutions = {}) {
  const entries = []

  for (const [plain, cipherVal] of Object.entries(substitutions)) {
    let cipher, isCanonical

    if (typeof cipherVal === 'object' && cipherVal !== null) {
      cipher = cipherVal.cipher
      isCanonical = cipherVal.canonical || false
    } else {
      cipher = cipherVal
      isCanonical = false
    }

    entries.push({ plain, cipher, isCanonical })
  }

  // Sort alphabetically by plain text
  return entries.sort((a, b) => a.plain.localeCompare(b.plain))
}

/**
 * Get character cipher description for display.
 * @param {Object} cipher - Cipher definition
 * @returns {string|null}
 */
export function getCharacterCipherDescription(cipher) {
  const cfg = cipher.characterCipher || cipher.character_cipher
  if (!cfg) return null

  const vowels = cfg.vowelRotation || cfg.vowel_rotation || ['A', 'E', 'I', 'O', 'U', 'Y']
  const shift = cfg.consonantShift || cfg.consonant_shift || 2

  return `Vowels rotate: ${vowels.join(' → ')} → ${vowels[0]}. Consonants shift +${shift}.`
}
